import { logger } from '@services/logger/logger.service';

export const FILE_BATCH_MIN_ELAPSED_TIME = 1000;
export const FILE_BATCH_POLLING_INTERVAL = 500;

/**
 * Collects file paths reported in quick succession and emits them as a single batch
 * once the stream has been quiet for `FILE_BATCH_MIN_ELAPSED_TIME`. One instance per
 * watcher channel — it holds no global state.
 */
export class FileBatchDebouncer {
  private watchFiles: string[];

  private lastWatchedFileDate: number | undefined;

  private timeoutId: NodeJS.Timeout | undefined;

  private onFilesReady: ((filePaths: string[]) => void) | null = null;

  public constructor() {
    this.watchFiles = [];
  }

  public setOnFilesReady(callback: (filePaths: string[]) => void): () => void {
    this.onFilesReady = callback;
    return () => {
      if (this.onFilesReady === callback) {
        this.onFilesReady = null;
      }
    };
  }

  public onWatchFile(filePath: string): void {
    this.watchFiles.push(filePath);
    this.lastWatchedFileDate = Date.now();

    if (!this.timeoutId) {
      this.scheduleFlush();
    }
  }

  private scheduleFlush() {
    this.timeoutId = setTimeout(() => {
      this.flushIfReady();
    }, FILE_BATCH_POLLING_INTERVAL);
  }

  private flushIfReady() {
    if (
      this.lastWatchedFileDate &&
      Date.now() - this.lastWatchedFileDate > FILE_BATCH_MIN_ELAPSED_TIME
    ) {
      const filePaths = [...this.watchFiles];
      if (filePaths.length > 1) {
        logger.info('LOGIC', `Watcher batch ready with ${filePaths.length} files.`);
      }

      this.onFilesReady?.(filePaths);

      this.watchFiles = [];
      this.lastWatchedFileDate = undefined;

      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = undefined;
    } else {
      this.scheduleFlush();
    }
  }
}
